import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { usePurchase } from "@/context/PurchaseContext";
import { HeartButton } from "@/components/HeartButton";
import { API } from "@/lib/api";
import { MiniWaveform } from "@/components/MiniWaveform";
import { IncludedFormats } from "@/components/IncludedFormats";
import { Play, Pause, ShoppingBag, Star, Check, Loader2, Layers } from "lucide-react";

export const FeaturedBeat = ({ track, owned }) => {
  const { current, isPlaying, playTrack } = usePlayer();
  const { openBuy, loadingId } = usePurchase();
  if (!track) return null;
  const isCurrent = current?.id === track.id;
  const loading = loadingId === track.id;

  return (
    <section data-testid="featured-beat" className="px-6 md:px-12 pt-10 relative z-10">
      <div className="relative overflow-hidden rounded-3xl border border-[#e2ff31]/30 bg-gradient-to-br from-[#161600] via-[#0a0a0a] to-[#0a0a0a] p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-8">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#e2ff31]/10 blur-3xl" aria-hidden="true" />
        <div className="relative w-40 h-40 md:w-52 md:h-52 shrink-0">
          <img src={`${API}${track.cover_url}`} alt={track.title} className="w-full h-full rounded-2xl object-cover" />
          <button data-testid="featured-play" onClick={() => playTrack(track, [track])}
            className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-2xl transition-colors">
            <span className="w-14 h-14 rounded-full bg-[#e2ff31] text-black flex items-center justify-center shadow-lg">
              {isCurrent && isPlaying ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6 ml-1" fill="currentColor" />}
            </span>
          </button>
        </div>

        <div className="relative min-w-0 flex-1 text-center md:text-left">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e2ff31] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 mb-3">
            <Star className="w-3.5 h-3.5" fill="currentColor" /> Featured beat
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight truncate">{track.title}</h2>
          <p className="text-[#9ca3af] mt-1">
            {track.producer}
            {track.bpm ? ` · ${track.bpm} BPM` : ""}
            {track.music_key ? ` · ${track.music_key}` : ""}
          </p>
          {track.waveform && track.waveform.length ? (
            <MiniWaveform peaks={track.waveform} bars={56} active={isCurrent} className="h-10 mt-4 max-w-md mx-auto md:mx-0" />
          ) : null}
          <div className="mt-5 flex items-center justify-center md:justify-start gap-3">
            {owned ? (
              <span className="flex items-center gap-1 text-[#22c55e] font-medium"><Check className="w-4 h-4" /> Owned</span>
            ) : (
              <>
                <button data-testid="featured-buy" onClick={() => openBuy(track)} disabled={loading}
                  className="flex items-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold px-6 py-3">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  Buy for ${track.price?.toFixed(2)}
                </button>
                <HeartButton track={track} />
                <span className="hidden sm:flex items-center gap-1.5 text-xs">
                  <Layers className="w-3.5 h-3.5 text-[#e2ff31]" /><IncludedFormats track={track} />
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
